import { StrictMode } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { Theme } from "./ui/Theme";
import { SessionProvider } from "./lib/session";
import { RequireUser, RequireClient } from "./ui/ProtectedRoute";
import { Login } from "./routes/Login";
import { Clientes } from "./routes/admin/Clientes";
import { Cadastro } from "./routes/Cadastro";
import { Cadastro as ConvitesAdmin } from "./routes/admin/Cadastro";
import { Desativados } from "./routes/admin/Desativados";
import { DashboardGeral } from "./routes/admin/DashboardGeral";
import { ClienteForm } from "./routes/admin/ClienteForm";
import { ClienteDetalhe } from "./routes/admin/ClienteDetalhe";
import { Esteira } from "./routes/admin/Esteira";
import { PostForm } from "./routes/admin/PostForm";
import { PostDetalhe } from "./routes/admin/PostDetalhe";
import { Kanban } from "./routes/admin/Kanban";
import { Dashboard } from "./routes/admin/Dashboard";
import { Mapas } from "./routes/admin/Mapas";
import { MapasGlobal } from "./routes/admin/MapasGlobal";
import { Mapa } from "./routes/admin/Mapa";
import { MapaPublico } from "./routes/MapaPublico";
import { ClientAccess } from "./routes/ClientAccess";
import { RelatorioPublico } from "./routes/RelatorioPublico";
import { Aprovacoes } from "./routes/client/Aprovacoes";
import { Desempenho } from "./routes/client/Desempenho";
import { Briefing } from "./routes/client/Briefing";
import { MeuMapa } from "./routes/client/MeuMapa";
import { BriefingView } from "./routes/admin/BriefingView";
import { Timeline } from "./routes/admin/Timeline";
import { Historico } from "./routes/admin/Historico";
import { OnboardingContentEditor } from "./routes/admin/OnboardingContentEditor";
import { ClientLogin } from "./routes/ClientLogin";
import { OnboardingWizard } from "./routes/OnboardingWizard";
import { OnboardingArchitecture } from "./routes/OnboardingArchitecture";
import { DesignSystem } from "./routes/DesignSystem";
import "./styles/index.css";

// Território escuro + guarda de equipe.
const admin = (node: ReactNode): ReactNode => (
  <Theme territory="admin">
    <RequireUser>{node}</RequireUser>
  </Theme>
);

const container = document.getElementById("root");
if (container === null) throw new Error("Elemento #root não encontrado.");

// PWA: registra o service worker só em produção (no dev atrapalharia o HMR).
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/entrar" replace />} />

          {/* Equipe — território escuro (ADR-008) */}
          <Route
            path="/entrar"
            element={
              <Theme territory="admin">
                <Login />
              </Theme>
            }
          />
          <Route
            path="/cadastro"
            element={
              <Theme territory="admin">
                <Cadastro />
              </Theme>
            }
          />
          <Route path="/convites" element={admin(<ConvitesAdmin />)} />
          <Route path="/gestao" element={admin(<Clientes />)} />
          <Route path="/desativados" element={admin(<Desativados />)} />
          <Route path="/dashboard" element={admin(<DashboardGeral />)} />
          <Route path="/clientes" element={<Navigate to="/gestao" replace />} />
          <Route path="/clientes/novo" element={admin(<ClienteForm />)} />
          <Route path="/clientes/:id" element={admin(<ClienteDetalhe />)} />
          <Route path="/clientes/:id/editar" element={admin(<ClienteForm />)} />
          <Route path="/clientes/:id/esteira" element={admin(<Esteira />)} />
          <Route path="/clientes/:id/esteira/novo" element={admin(<PostForm />)} />
          <Route path="/clientes/:id/kanban" element={admin(<Kanban />)} />
          <Route path="/clientes/:id/dashboard" element={admin(<Dashboard />)} />
          <Route path="/clientes/:id/mapas" element={admin(<Mapas />)} />
          <Route path="/clientes/:id/mapas/:mapId" element={admin(<Mapa />)} />
          <Route path="/mapas" element={admin(<MapasGlobal />)} />
          <Route path="/mapas/:mapId" element={admin(<Mapa />)} />
          <Route path="/gestao/mapas" element={admin(<MapasGlobal />)} />
          <Route path="/clientes/:id/briefing" element={admin(<BriefingView />)} />
          <Route path="/clientes/:id/timeline" element={admin(<Timeline />)} />
          <Route path="/historico" element={admin(<Historico />)} />
          <Route path="/arquitetura-onboarding" element={admin(<OnboardingContentEditor />)} />
          <Route path="/clientes/:id/posts/:postId" element={admin(<PostDetalhe />)} />
          <Route
            path="/dev/design-system"
            element={
              <Theme territory="admin">
                <DesignSystem />
              </Theme>
            }
          />

          {/* Cliente — território creme */}
          <Route
            path="/c/:token"
            element={
              <Theme territory="client">
                <ClientAccess />
              </Theme>
            }
          />
          <Route
            path="/mapa/:token"
            element={
              <Theme territory="client">
                <MapaPublico />
              </Theme>
            }
          />
          <Route
            path="/relatorio/:token"
            element={
              <Theme territory="client">
                <RelatorioPublico />
              </Theme>
            }
          />
          <Route
            path="/onboarding/:token"
            element={
              <Theme territory="client">
                <OnboardingWizard />
              </Theme>
            }
          />
          <Route
            path="/entrar-cliente"
            element={
              <Theme territory="client">
                <ClientLogin />
              </Theme>
            }
          />
          {/* A Arquitetura de Onboarding aplica o próprio Theme internamente
              (é a marca escura da Lara, não o creme do território da cliente). */}
          <Route path="/arquitetura/:token" element={<OnboardingArchitecture />} />
          <Route path="/arquitetura-preview/:token" element={<OnboardingArchitecture preview />} />
          <Route
            path="/aprovacoes"
            element={
              <Theme territory="client">
                <RequireClient>
                  <Aprovacoes />
                </RequireClient>
              </Theme>
            }
          />
          <Route
            path="/meu-mapa"
            element={
              <Theme territory="client">
                <RequireClient>
                  <MeuMapa />
                </RequireClient>
              </Theme>
            }
          />
          <Route
            path="/desempenho"
            element={
              <Theme territory="client">
                <RequireClient>
                  <Desempenho />
                </RequireClient>
              </Theme>
            }
          />
          <Route
            path="/briefing"
            element={
              <Theme territory="client">
                <RequireClient>
                  <Briefing />
                </RequireClient>
              </Theme>
            }
          />

          <Route path="*" element={<Navigate to="/entrar" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  </StrictMode>,
);
